/**
 * What an Ory access token says about the person, read on the device. The token is a JWT; its
 * signature is Core's and Reach's to check, never the app's, so nothing here decides access.
 */

/** base64url → UTF-8 text with `atob` alone, which every runtime here has (Hermes included). */
function base64UrlDecode(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  let escaped = '';
  for (let index = 0; index < binary.length; index += 1) {
    escaped += `%${binary.charCodeAt(index).toString(16).padStart(2, '0')}`;
  }
  return decodeURIComponent(escaped);
}

/** The token's claims, or undefined when it isn't a readable JWT. */
export function tokenClaims(token: string): Record<string, unknown> | undefined {
  const payload = token.split('.')[1];
  if (!payload) return undefined;
  try {
    const claims: unknown = JSON.parse(base64UrlDecode(payload));
    return typeof claims === 'object' && claims !== null && !Array.isArray(claims)
      ? (claims as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The account's home region (`au`, `us`) from its token's claims: `home_region` at the top
 * level, or under `ext`, where Ory puts session claims it isn't told to lift (platform
 * identity.md 3.1). Undefined when the token carries neither. OnEarly `app` reads it the same
 * way (`getTokenHomeRegion`).
 */
export function homeRegionOfClaims(
  claims: Record<string, unknown> | undefined,
): string | undefined {
  if (!claims) return undefined;
  const ext = claims.ext;
  const home =
    claims.home_region ??
    (typeof ext === 'object' && ext !== null
      ? (ext as Record<string, unknown>).home_region
      : undefined);
  return typeof home === 'string' && home !== '' ? home : undefined;
}

/** `homeRegionOfClaims` for a raw access token. */
export function homeRegionOfToken(token: string): string | undefined {
  return homeRegionOfClaims(tokenClaims(token));
}
